import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import CouponSection from '../CouponSection/CouponSection';
import Disclaimers from '../../../assets/img/disclaimer-icon.svg';
import Box from '@mui/material/Box';

function PaymentGrid({ subtotal = 0, discount = 0, total = 0, disableContinueButton=false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isCheckoutPage = location.pathname.includes('/checkout');
  const coupon = useSelector(state => state.cart?.coupon);
  
  // Get cart items from Redux store
  const endorsements = useSelector((state) => state.wizardLaunch?.endorsements) || [];
  const moreClassSelection = useSelector((state) => state.wizardLaunch?.moreClassSelection) || [];
  const directCourses = useSelector((state) => state.wizardLaunch?.directCourses) || [];
  
  // Check if cart has items, regardless of price
  const hasCartItems = endorsements.length > 0 || moreClassSelection.length > 0 || directCourses.length > 0;

  // Add states for dialogs
  const [openEmptyCartDialog, setOpenEmptyCartDialog] = useState(false);
  const [openZeroPriceDialog, setOpenZeroPriceDialog] = useState(false);

  // Handler functions for dialogs
  const handleCloseDialog = () => {
    setOpenEmptyCartDialog(false);
    setOpenZeroPriceDialog(false);
  };

  // Add these state variables
  const [openDisclaimerDialog, setOpenDisclaimerDialog] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);

  // Update the handleContinue function
  const handleContinue = () => {
    if (!hasCartItems) {
      setOpenEmptyCartDialog(true);
      return;
    }
    if (numericSubtotal === 0) {
      setOpenZeroPriceDialog(true);
      return;
    }
    // Show disclaimer dialog instead of navigating directly
    setOpenDisclaimerDialog(true);
  };

  // Add this new function
  const handleDisclaimerConfirm = () => {
    setOpenDisclaimerDialog(false);
    navigate('/checkout');
  };

  // Convert string values to numbers and handle any non-numeric values
  const numericSubtotal = Number(subtotal) || 0;
  const numericDiscount = Number(discount) || 0;
  const numericTotal = Number(total) || 0;

  // Get coupon discount directly from Redux state
  const couponDiscount = coupon?.discount || 0;

  return (
    <div className="right-grid-container">
      <div className="payment-sec">
        <Typography component="h3" className="heading-sm">
          Payment
        </Typography>
        
        <CouponSection />

        <div className="total-price-wrap">
          <div className="subtotal-div">
            <span className="lbl-wdt">SubTotal</span>
            <span className="line"></span>
            <span className="lbl-wdt-pp">${numericSubtotal?.toFixed(2)}</span>
          </div>

        {discount > 0 && (
          <div className="subtotal-div">
            <span className="lbl-wdt">Discount</span>
            <span className="line"></span>
            <span className="lbl-wdt-pp">${numericDiscount?.toFixed(2)}</span>
          </div>
        )}
          {couponDiscount > 0 && (
            <div className="subtotal-div">
              <span className="lbl-wdt">Coupon Discount</span>
              <span className="line"></span>
              <span className="lbl-wdt-pp">${couponDiscount.toFixed(2)}</span>
            </div>
          )}
          <div className="subtotal-div">
            <span className="lbl-wdt">
              <strong>Total</strong>
            </span>
            <span className="line"></span>
            <span className="lbl-wdt-pp">
              <strong>${numericTotal.toFixed(2)}</strong>
            </span>
          </div>

          {!isCheckoutPage && (
            <Button 
              variant="contained" 
              color="primary" 
              fullWidth 
              onClick={handleContinue}
              className="btn-continue"
              disabled={disableContinueButton}
            >
              Continue
            </Button>
          )}
          
          <Typography component="div" className="secure-check-lbl">
            <LockOutlinedIcon />
            Secure Checkout - SSL Encrypted
          </Typography>
          <Typography component="p" className="secure-check-p">
            Ensuring your finance and personal details are secure during
            every transaction
          </Typography>
        </div>
      </div>
      
      <Dialog
        open={openEmptyCartDialog}
        onClose={handleCloseDialog}
        aria-labelledby="empty-cart-dialog"
      >
        <DialogTitle>Empty Shopping Cart</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your shopping cart is empty. Please add courses before proceeding to payment.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={openZeroPriceDialog}
        onClose={handleCloseDialog}
        aria-labelledby="zero-price-dialog"
      >
        <DialogTitle>Zero Price Cart</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your shopping cart price is 0. Please add courses before proceeding to payment.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    
          <Dialog
        className='disclaimers-modal'
        open={openDisclaimerDialog}
        onClose={() => setOpenDisclaimerDialog(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            position: 'relative',
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            color: '#0A2647',
            fontWeight: 'bold',
            fontSize: '24px',
            pb: 1,
          }}
        >
          <img src={Disclaimers} alt='Disclaimers Icon' />
          Disclaimers & Acknowledgments
        </DialogTitle>
        
        <DialogContent  sx={{ overflow: 'hidden'}}>
          <Box sx={{ py: 2, px: 2 }}>
            <Typography variant="body1" gutterBottom sx={{ pl: 2,     fontSize: '0.9rem' }}>
              This online ELDT Theory course is only the first step in earning your Commercial Driver's License (CDL). To become fully licensed, you'll also need to:
            </Typography>
      
            <ul style={{ listStyleType: 'disc', paddingLeft: '36px', marginTop: '10px' }}>
              <li style={{marginBottom: '7px'}}>Obtain your Commercial Learner's Permit (CLP) (if you haven't already)</li>
              <li style={{marginBottom: '7px'}}>Complete Behind-the-Wheel (Range and Road) Training</li>
              <li style={{marginBottom: '7px'}}>Pass the CDL Skills Test through your state licensing agency</li>
            </ul>
            <Typography variant="body1" gutterBottom sx={{ pl: 2  , fontSize: '0.9rem' }}>
              If you need help with the next steps, our team is here to support you.
            </Typography>
      
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 4, pl: 2 }}>
              <input
                type="checkbox"
                id="disclaimerCheckbox"
                checked={disclaimerChecked}
                onChange={(e) => setDisclaimerChecked(e.target.checked)}
                style={{ marginRight: '10px', marginTop: '4px' }}
              />
              <Typography variant="body2">
                <i>I understand this course covers only the ELDT theory requirement, includes electronic reporting to the FMCSA, and that additional training and testing are required to obtain a CDL.</i>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      
        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 4,
            px: 3,
            gap: 2,
          }}
        >
          <Button
            className='accept-btn'
            variant='contained'
            onClick={handleDisclaimerConfirm}
            disabled={!disclaimerChecked}
            sx={{
              backgroundColor: '#0D47A1',
              '&:hover': {
                backgroundColor: '#0A2647',
              },
              textTransform: 'none',
              fontSize: '16px',
              px: 4,
              opacity: disclaimerChecked ? 1 : 0.3,
              cursor: disclaimerChecked ? 'pointer' : 'not-allowed',
            }}
          >
            Accept and Continue
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

PaymentGrid.propTypes = {
  subtotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  discount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default PaymentGrid;